import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CategoryTerm } from '../../../infrastructure/SkinSeoulProductsClient';
import { skinSeoulProductsClient } from '../../../infrastructure/SkinSeoulProductsClient';

const PATH_SEPARATOR = ' > ';
const MAX_ANCESTRY_DEPTH = 20;

export interface CategoryOption {
  termId: number;
  slug: string;
  name: string;
  fullPath: string;
}

export interface ProductCatTaxonomy {
  options: CategoryOption[];
  pathBySlug: Map<string, string>;
  hasSlug: (slug: string) => boolean;
  isLoading: boolean;
  isError: boolean;
}

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#0?39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&#8250;/g, '>');

const buildFullPath = (term: CategoryTerm, termById: Map<number, CategoryTerm>): string => {
  const names: string[] = [decodeHtmlEntities(term.name)];
  let parentTermId = term.parentTermId;
  let depth = 0;
  while (parentTermId && depth < MAX_ANCESTRY_DEPTH) {
    const parent = termById.get(parentTermId);
    if (!parent) {
      break;
    }
    names.unshift(decodeHtmlEntities(parent.name));
    parentTermId = parent.parentTermId;
    depth += 1;
  }
  return names.join(PATH_SEPARATOR);
};

export const useProductCatTaxonomy = (): ProductCatTaxonomy => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['productCatTaxonomy'],
    queryFn: () => skinSeoulProductsClient.getAllCategories(),
    staleTime: Infinity,
  });

  return useMemo(() => {
    const categories = (data ?? []).filter((term) => term.taxonomy === 'product_cat');
    const termById = new Map(categories.map((term) => [term.termId, term]));
    const options = categories
      .map((term) => ({
        termId: term.termId,
        slug: term.slug,
        name: decodeHtmlEntities(term.name),
        fullPath: buildFullPath(term, termById),
      }))
      .sort((first, second) => first.fullPath.localeCompare(second.fullPath));
    const pathBySlug = new Map(options.map((option) => [option.slug, option.fullPath]));
    return {
      options,
      pathBySlug,
      hasSlug: (slug: string) => pathBySlug.has(slug),
      isLoading,
      isError,
    };
  }, [data, isLoading, isError]);
};
